/**
 * Hides Space inbox rows the viewer cannot decrypt, and reveals them once a
 * Cipher is linked for that channel.
 */

import { useMemo, useSyncExternalStore } from 'react';
import type { InboxItem } from '@adieuu/shared';
import { useCipherStore } from './useCipherStore';
import { useSpaces } from './useSpaces';
import {
  getCipherLinkEpoch,
  subscribeCipherLinks,
} from '../services/spaceCipherService';
import {
  inboxItemIsReadable,
  readableChannelRef,
  readableSpaceRef,
} from '../services/spaceChannelReadability';

export function useReadableSpaceInboxItems(items: readonly InboxItem[]): InboxItem[] {
  const cipherEpoch = useSyncExternalStore(subscribeCipherLinks, getCipherLinkEpoch, () => 0);
  const { spaces, channels } = useSpaces();
  const { getCipherKey } = useCipherStore();

  return useMemo(
    () =>
      items.filter((item) =>
        inboxItemIsReadable(item, {
          spaces: spaces.flatMap((space) => {
            const ref = readableSpaceRef(space);
            return ref ? [ref] : [];
          }),
          channels: channels.map(readableChannelRef),
          getCipherKey,
        }),
      ),
    [items, spaces, channels, getCipherKey, cipherEpoch],
  );
}
